import { useCallback, useState, useEffect } from 'react';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, OpenInNew, Psychology, ContentCopy } from '@mui/icons-material';
import { Badge, Button, IconButton, Tooltip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { LoadingState } from '../components/loading-state';
import { useGetProviderNoteProcessingSessionsQuery, useGetProviderInfoQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

export default function NoteProcessingSessionsListPage() {
  const navigate = useNavigate();
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const copy = useCopyToClipboard();

  // State for pagination and filtering
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Memoized filter change handler
  const handleFiltersChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  // Memoized sort change handler
  const handleSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1); // Reset to first page when sort changes
  }, []);

  const getStatusBadgeColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'active':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'draft':
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Get URL for each row
  const getRowUrl = useCallback((nps: any) => {
    return `/admin-v2/note-processing-session/${nps.note_processing_session_id}`;
  }, []);

  // Fetch provider info if providerId is available
  const { data: provider, isLoading: providerLoading } = useGetProviderInfoQuery(
    { providerId: providerId || '' },
    { skip: !providerId },
  );

  // Fetch provider-specific note processing sessions with pagination and filtering
  const { data: npsData, isLoading: npsLoading } = useGetProviderNoteProcessingSessionsQuery(
    {
      providerId: providerId || '',
      page,
      perPage,
      ...filters,
    },
    { skip: !providerId },
  );

  const npsColumns = [
    {
      key: 'note_processing_session_id',
      label: 'Session ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">{value}</span>
          <Tooltip title="Copy ID">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                copy(value, 'Session ID');
              }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'provider_id',
      label: 'Provider',
      render: (value: string) => (
        <Link
          to={`/admin-v2/provider/${encodeURIComponent(value)}`}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          {value.substring(0, 8)}...
        </Link>
      ),
    },
    {
      key: 'encounter_id',
      label: 'Encounter',
      render: (value: string | null) =>
        value ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-900 break-all font-mono">{value}</span>
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  copy(value, 'Encounter ID');
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        ),
    },
    {
      key: 'transcription_session_ids',
      label: 'Transcripts',
      render: (value: string[] | null) => (
        <span className="text-sm text-gray-900">{value?.length || 0}</span>
      ),
    },
    {
      key: 'note_id',
      label: 'Note',
      render: (value: string | null) =>
        value ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-green-600 break-all font-mono">{value}</span>
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  if (value) copy(value, 'Transcript ID');
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          <span className="text-sm text-gray-400">No note</span>
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
  ];

  const filterConfigs = [
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'processing', label: 'Processing' },
        { value: 'completed', label: 'Completed' },
        { value: 'failed', label: 'Failed' },
      ],
    },
    {
      key: 'providerId',
      label: 'Provider ID',
      type: 'text' as const,
    },
    {
      key: 'encounterId',
      label: 'Encounter ID',
      type: 'text' as const,
    },
    {
      key: 'transcriptionSessionId',
      label: 'Transcript ID',
      type: 'text' as const,
    },
    {
      key: 'noteId',
      label: 'Note ID',
      type: 'text' as const,
    },
  ];

  // Show error if no providerId
  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider Required</h2>
            <p className="text-gray-600 mt-2">
              A provider ID is required to view note processing sessions.
            </p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (npsLoading || providerLoading) {
    return <LoadingState message="Loading note processing sessions..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            {provider && (
              <li>
                <div className="flex items-center">
                  <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                  <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                    <Button
                      variant="text"
                      className="text-sm font-medium text-gray-700 hover:text-blue-600"
                    >
                      {provider.name}
                    </Button>
                  </Link>
                </div>
              </li>
            )}
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                  Note Processing Sessions
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <Psychology className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">Note Processing Sessions</h1>
                  <p className="text-gray-600">
                    {provider
                      ? `All note processing sessions for ${provider.name}`
                      : 'Loading provider...'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Sessions</div>
                <div className="text-2xl font-bold text-gray-900">{npsData?.total || 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filterable Data Table */}
        <FilterableDataTable
          title="Note Processing Sessions"
          columns={npsColumns as any}
          data={npsData?.items || []}
          total={npsData?.total}
          loading={npsLoading}
          page={page}
          perPage={perPage}
          filters={filterConfigs}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onFiltersChange={handleFiltersChange}
          onSortChange={handleSortChange}
          getRowUrl={getRowUrl}
          emptyMessage="No note processing sessions to display"
        />
      </main>
    </div>
  );
}
